import { Controller } from 'stimulus';
import axios from 'axios';
import Floorplan from '../../components/floorplan';

export default class extends Controller {
  static targets = [
    'locationModeField',
    'childLocationIdField',
    'xField',
    'yField',
    'locationSelect',
    'transitLocationSelect',
    'storeLocationSelect',
  ];

  connect() {
    this.floorplanInstance = null;
    this.state = {
      mode: 'building',
      locationId: null,
      childLocationId: null,
      floorplanLocationId: null,
      floorplanObject: null,
      population: null,
    };
    this.loadLocations('building');
  }

  locationModeChanged(event) {
    if (event.preventDefault) event.preventDefault();
    const mode =
      event.currentTarget.dataset.mode ||
      event.currentTarget.closest('.location-mode-btn')?.dataset.mode ||
      'building';
    this.state.mode = mode;
    if (this.hasLocationModeFieldTarget) {
      this.locationModeFieldTarget.value = mode;
    }

    this.element.querySelectorAll('.location-mode-btn').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.mode === mode);
    });

    this.clearChildrenDropdown();
    this.hideFloorplan();
    this.clearCoordinates();
    this.updateLocationUI();
    this.loadLocations(mode);
  }

  locationChanged(event) {
    const value = event.target.value;
    const mode = this.state.mode;

    if (!value) {
      this.resetLocationState();
      return;
    }

    if (mode === 'transit') {
      this.state.locationId = value;
      this.state.childLocationId = null;
      if (this.hasChildLocationIdFieldTarget) this.childLocationIdFieldTarget.value = '';
      this.hideFloorplan();
      this.clearCoordinates();
      return;
    }

    if (mode === 'store') {
      this.state.locationId = value;
      this.state.childLocationId = null;
      if (this.hasChildLocationIdFieldTarget) this.childLocationIdFieldTarget.value = '';
      this.hideFloorplan();
      this.hideNoFloorplanHelp();
      this.clearCoordinates();
      this.loadLocationDetails(value);
      return;
    }

    this.state.locationId = value;
    this.state.childLocationId = null;
    if (this.hasChildLocationIdFieldTarget) this.childLocationIdFieldTarget.value = '';
    this.loadLocationDetails(value);
  }

  childLocationChanged(event) {
    const select = event.target;
    const level = Number(select.dataset.level) || 1;
    const value = select.value || null;
    const container = document.getElementById('bulk-location-children-container');

    const removeDeeper = (fromLevel) => {
      if (!container) return;
      container.querySelectorAll('select[data-level]').forEach((s) => {
        const l = Number(s.dataset.level) || 0;
        if (l >= fromLevel) {
          this.destroySearchableDropdown(s);
          s.remove();
        }
      });
    };

    removeDeeper(level + 1);

    if (!value) {
      this.state.childLocationId = null;
      if (this.hasChildLocationIdFieldTarget) this.childLocationIdFieldTarget.value = '';
      this.state.floorplanLocationId = null;
      this.state.floorplanObject = null;
      this.hideFloorplan();
      this.clearCoordinates();
      return;
    }

    this.state.childLocationId = value;
    if (this.hasChildLocationIdFieldTarget) {
      this.childLocationIdFieldTarget.value = value;
    }

    axios
      .get(`/locations/${encodeURIComponent(value)}.json`)
      .then((response) => {
        const data = response.data.location || {};
        if (data.children && data.children.length > 0) {
          this.applyFloorplanFromLocation(data);
          this.populateChildrenDropdown(data, level + 1, true);
        } else {
          this.applyFloorplanFromLocation(data);
        }
      })
      .catch((error) => {
        console.error('Failed to load child location:', error);
      });
  }

  loadLocations(mode) {
    const type = mode === 'transit' ? 'transit' : mode === 'store' ? 'store' : 'building';
    axios
      .get(`/locations.json?type=${encodeURIComponent(type)}`)
      .then((response) => {
        this.populateLocationsForMode(mode, response.data || []);
      })
      .catch(() => {
        this.populateLocationsForMode(mode, []);
      });
  }

  populateLocationsForMode(mode, locations) {
    let select = null;
    if (mode === 'transit' && this.hasTransitLocationSelectTarget) {
      select = this.transitLocationSelectTarget;
    } else if (mode === 'store' && this.hasStoreLocationSelectTarget) {
      select = this.storeLocationSelectTarget;
    } else if (mode === 'building' && this.hasLocationSelectTarget) {
      select = this.locationSelectTarget;
    }
    if (!select) return;

    while (select.firstChild) select.removeChild(select.firstChild);

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent =
      mode === 'transit'
        ? 'Select a Transit Location'
        : mode === 'store'
          ? 'Select a StoreMe Location'
          : 'Select a Location';
    select.appendChild(placeholder);

    locations.forEach((loc) => {
      const opt = document.createElement('option');
      opt.value = loc.id;
      opt.textContent = loc.absolute_path_name || loc.name;
      select.appendChild(opt);
    });

    this.ensureSearchableDropdown(select, { placeholder: placeholder.textContent });
  }

  loadLocationDetails(locationId) {
    return axios
      .get(`/locations/${encodeURIComponent(locationId)}.json`)
      .then((response) => {
        const locationData = response.data.location || {};
        this.state.population = locationData.population;

        if (locationData.children && locationData.children.length > 0) {
          this.populateChildrenDropdown(locationData.children);
        } else {
          this.clearChildrenDropdown();
        }

        if (this.state.mode === 'building') {
          if (locationData.floorplan && locationData.floorplan.url) {
            this.state.floorplanLocationId = locationData.id;
            this.state.floorplanObject = locationData.floorplan;
            this.renderFloorplan();
          } else {
            this.resolveFloorplanBindingFromLocationId(locationId).then((binding) => {
              if (binding) {
                this.state.floorplanLocationId = binding.floorplanLocationId;
                this.state.floorplanObject = binding.floorplan;
                this.state.population = binding.population;
                this.renderFloorplan();
              } else {
                this.showNoFloorplanHelp(locationData.id);
                this.hideFloorplan();
              }
            });
          }
        } else if (this.state.mode === 'store') {
          this.hideFloorplan();
          this.hideNoFloorplanHelp();
        }
        return locationData;
      })
      .catch((error) => {
        console.error('Failed to load location details:', error);
        this.clearChildrenDropdown();
      });
  }

  applyFloorplanFromLocation(data) {
    const mode = this.state.mode;
    if (mode !== 'building') {
      this.hideFloorplan();
      this.hideNoFloorplanHelp();
      this.clearCoordinates();
      return;
    }

    const showPlan = (locId, floorplan, population) => {
      this.state.floorplanLocationId = locId;
      this.state.floorplanObject = floorplan;
      this.state.population = population;
      this.clearCoordinates();
      this.renderFloorplan();
      this.hideNoFloorplanHelp();
    };

    if (data.floorplan && data.floorplan.url) {
      showPlan(data.id, data.floorplan, data.population);
      return;
    }

    this.resolveFloorplanBindingFromLocationId(data.id).then((binding) => {
      if (binding) {
        showPlan(binding.floorplanLocationId, binding.floorplan, binding.population);
      } else {
        this.showNoFloorplanHelp(data.id);
        this.hideFloorplan();
      }
    });
  }

  resolveFloorplanBindingFromLocationId(locationId) {
    if (!locationId) return Promise.resolve(null);
    return axios.get(`/locations/${encodeURIComponent(locationId)}.json`).then((response) => {
      const loc = response.data.location || {};
      if (loc.floorplan && loc.floorplan.url) {
        return {
          floorplanLocationId: loc.id,
          floorplan: loc.floorplan,
          population: loc.population || null,
        };
      }
      if (loc.parent && loc.parent.id) {
        return this.resolveFloorplanBindingFromLocationId(loc.parent.id);
      }
      return null;
    });
  }

  populateChildrenDropdown(children, level = 1, append = false) {
    const container = document.getElementById('bulk-location-children-container');
    if (!container) return;

    let items = [];
    if (Array.isArray(children)) items = children;
    else if (children && typeof children === 'object') {
      items = children.children || [children];
    }

    if (!append && level === 1) {
      container.querySelectorAll('select[data-level]').forEach((s) => {
        this.destroySearchableDropdown(s);
        s.remove();
      });
      container.style.display = items.length > 0 ? 'block' : 'none';
    } else {
      container.style.display = 'block';
    }

    if (items.length === 0) return;

    const select = document.createElement('select');
    select.className = 'form-control select';
    select.dataset.level = String(level);
    container.appendChild(select);

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Select a Sub Location';
    select.appendChild(placeholder);

    items.forEach((child) => {
      const option = document.createElement('option');
      option.value = child.id;
      option.textContent = child.absolute_path_name || child.name || '';
      select.appendChild(option);
    });

    this.ensureSearchableDropdown(select, { placeholder: 'Select a Sub Location' });
    select.addEventListener('change', (e) => this.childLocationChanged(e));
  }

  clearChildrenDropdown() {
    const container = document.getElementById('bulk-location-children-container');
    if (!container) return;
    container.querySelectorAll('select[data-level]').forEach((s) => {
      this.destroySearchableDropdown(s);
      s.remove();
    });
    container.style.display = 'none';
    this.state.childLocationId = null;
    if (this.hasChildLocationIdFieldTarget) this.childLocationIdFieldTarget.value = '';
  }

  renderFloorplan() {
    const floorplan = this.state.floorplanObject;
    if (!floorplan || !floorplan.url) {
      this.hideFloorplan();
      return;
    }

    const top = document.getElementById('bulk-location-floorplan-top');
    const container = document.getElementById('bulk-location-floorplan-container');
    if (!container) return;

    if (top) top.style.display = 'block';
    container.style.display = 'block';
    this.hideNoFloorplanHelp();

    let itemsData = '[]';
    if (this.state.population) {
      itemsData =
        typeof this.state.population === 'string'
          ? this.state.population
          : JSON.stringify(this.state.population);
    }

    container.innerHTML = `
      <div data-floorplan=""
           data-view-mode="write"
           data-type="location"
           data-model="item"
           data-placement-mode="single"
           data-plan="${floorplan.url}"
           data-width="${floorplan.width || 0}"
           data-height="${floorplan.height || 0}"
           data-items='${itemsData}'
           data-item='{}'
           style="height: 600px;">
        <div class="placeholder">
          <div class="placeholder-message">Initializing floorplan…</div>
        </div>
      </div>
    `;

    setTimeout(() => {
      try {
        this.floorplanInstance = new Floorplan();
        this.floorplanInstance.initialize();
      } catch (e) {
        console.error('Failed to initialize bulk location floorplan:', e);
      }
    }, 100);
  }

  hideFloorplan() {
    const top = document.getElementById('bulk-location-floorplan-top');
    const container = document.getElementById('bulk-location-floorplan-container');
    if (top) top.style.display = 'none';
    if (container) {
      container.style.display = 'none';
      container.innerHTML = '';
    }
    this.floorplanInstance = null;
  }

  showNoFloorplanHelp(locationId) {
    const el = document.getElementById('bulk-location-no-floorplan');
    const link = document.getElementById('bulk-location-edit-location-link');
    if (el) el.style.display = 'block';
    if (link && locationId) {
      link.href = `/locations/${locationId}/edit`;
    }
  }

  hideNoFloorplanHelp() {
    const el = document.getElementById('bulk-location-no-floorplan');
    if (el) el.style.display = 'none';
  }

  clearCoordinates() {
    if (this.hasXFieldTarget) this.xFieldTarget.value = '';
    if (this.hasYFieldTarget) this.yFieldTarget.value = '';
  }

  resetLocationState() {
    this.state.locationId = null;
    this.state.childLocationId = null;
    this.state.floorplanLocationId = null;
    this.state.floorplanObject = null;
    this.clearChildrenDropdown();
    this.hideFloorplan();
    this.hideNoFloorplanHelp();
    this.clearCoordinates();
  }

  updateLocationUI() {
    const mode = this.state.mode;
    this.element.querySelectorAll('.location-mode-dropdown').forEach((dropdown) => {
      dropdown.style.display = dropdown.dataset.mode === mode ? 'block' : 'none';
    });
  }

  ensureSearchableDropdown(selectEl, { placeholder = null } = {}) {
    if (!selectEl || selectEl.tagName !== 'SELECT') return;
    if (selectEl.dataset.sdInit === 'true') {
      this.syncSearchableDropdown(selectEl, { placeholder });
      return;
    }

    selectEl.dataset.sdInit = 'true';
    selectEl.style.display = 'none';

    const root = document.createElement('div');
    root.className = 'sd-root';
    root.style.position = 'relative';

    const trigger = document.createElement('input');
    trigger.type = 'text';
    trigger.className = selectEl.className || 'form-control select';
    trigger.autocomplete = 'off';
    trigger.readOnly = true;
    trigger.style.width = '100%';

    const panel = document.createElement('div');
    panel.style.cssText =
      'position:absolute;left:0;right:0;top:100%;z-index:9999;background:#fff;border:1px solid rgba(0,0,0,0.15);border-top:0;max-height:260px;overflow:auto;display:none';

    const list = document.createElement('div');
    panel.appendChild(list);
    selectEl.insertAdjacentElement('afterend', root);
    root.appendChild(trigger);
    root.appendChild(panel);

    const phDefault = placeholder || selectEl.options?.[0]?.textContent || 'Select…';
    const close = () => {
      panel.style.display = 'none';
      trigger.readOnly = true;
      this.updateSearchableDropdownLabel(selectEl, { placeholder: phDefault });
    };
    const open = () => {
      panel.style.display = 'block';
      trigger.readOnly = false;
      trigger.value = '';
      trigger.placeholder = 'Type to search…';
      this.syncSearchableDropdown(selectEl, { placeholder: phDefault });
      trigger.focus();
    };

    selectEl._sd = { root, trigger, panel, list, close, open };
    trigger.addEventListener('mousedown', (e) => {
      e.stopPropagation();
      if (panel.style.display !== 'block') open();
    });
    list.addEventListener('click', (e) => {
      const row = e.target.closest('.sd-option');
      if (!row) return;
      selectEl.value = row.dataset.value;
      selectEl.dispatchEvent(new Event('change', { bubbles: true }));
      close();
    });
    selectEl.addEventListener('change', () => {
      this.updateSearchableDropdownLabel(selectEl, { placeholder: phDefault });
    });
    this.syncSearchableDropdown(selectEl, { placeholder: phDefault });
  }

  syncSearchableDropdown(selectEl, { placeholder = null } = {}) {
    if (!selectEl || !selectEl._sd) return;
    const { list } = selectEl._sd;
    while (list.firstChild) list.removeChild(list.firstChild);
    const ph = placeholder || selectEl.options?.[0]?.textContent || 'Select…';
    Array.from(selectEl.options || [])
      .filter((o, idx) => !(idx === 0 && o.value === ''))
      .forEach((o) => {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'sd-option';
        row.dataset.value = o.value;
        row.textContent = o.textContent || '';
        row.style.cssText = 'display:block;width:100%;text-align:left;padding:0.5rem 0.75rem;border:0;background:transparent';
        list.appendChild(row);
      });
    this.updateSearchableDropdownLabel(selectEl, { placeholder: ph });
  }

  updateSearchableDropdownLabel(selectEl, { placeholder = null } = {}) {
    if (!selectEl || !selectEl._sd) return;
    const { trigger } = selectEl._sd;
    const ph = placeholder || selectEl.options?.[0]?.textContent || 'Select…';
    trigger.value = selectEl.value ? selectEl.selectedOptions?.[0]?.textContent || '' : '';
    trigger.placeholder = ph;
  }

  destroySearchableDropdown(selectEl) {
    if (!selectEl || !selectEl._sd) return;
    try {
      selectEl._sd.root.remove();
    } catch (e) {
      /* ignore */
    }
    selectEl.style.display = '';
    delete selectEl._sd;
    delete selectEl.dataset.sdInit;
  }
}
